import React, { useState } from 'react';
import { Card, Table, Button, Input, Select, DatePicker, Space, Tag, Modal, Form, message, Badge, Tabs } from 'antd';
import { SearchOutlined, BellOutlined, ExclamationCircleOutlined, CheckCircleOutlined, CloseCircleOutlined, FireOutlined, TeamOutlined, CarOutlined, SettingOutlined, EyeOutlined } from '@ant-design/icons';
import moment from 'moment';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { TabPane } = Tabs;

const WarningManagement = () => {
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [activeTab, setActiveTab] = useState('all');

  // 模拟数据
  const warningData = [
    {
      id: 1,
      type: 'person_detection',
      typeName: '人流检测',
      level: 'high',
      content: '前门人流量超过阈值，当前人数：35人',
      status: 'unprocessed',
      cameraName: '前门监控',
      timestamp: '2025-03-18 14:30:25',
      screenshot: 'https://via.placeholder.com/150',
    },
    {
      id: 2,
      type: 'fire_detection',
      typeName: '火灾检测',
      level: 'critical',
      content: '检测到可能的火灾隐情，请立即确认',
      status: 'processing',
      cameraName: '仓库监控',
      timestamp: '2025-03-18 13:45:12',
      screenshot: 'https://via.placeholder.com/150',
    },
    {
      id: 3,
      type: 'vehicle_detection',
      typeName: '车辆检测',
      level: 'medium',
      content: '停车场入口处车辆拥堵',
      status: 'processed',
      cameraName: '停车场监控',
      timestamp: '2025-03-18 12:20:33',
      screenshot: 'https://via.placeholder.com/150',
    },
    {
      id: 4,
      type: 'person_detection',
      typeName: '人流检测',
      level: 'low',
      content: '电梯区域人流量异常',
      status: 'processed',
      cameraName: '电梯监控',
      timestamp: '2025-03-18 11:15:45',
      screenshot: 'https://via.placeholder.com/150',
    },
    {
      id: 5,
      type: 'system',
      typeName: '系统警告',
      level: 'medium',
      content: '摄像头连接异常，请检查网络',
      status: 'unprocessed',
      cameraName: '后门监控',
      timestamp: '2025-03-18 10:05:18',
      screenshot: null,
    },
  ];

  // 表格列定义
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: '类型',
      dataIndex: 'typeName',
      key: 'typeName',
      render: (text, record) => {
        let icon = <ExclamationCircleOutlined />;
        if (record.type === 'fire_detection') {
          icon = <FireOutlined />;
        } else if (record.type === 'person_detection') {
          icon = <TeamOutlined />;
        } else if (record.type === 'vehicle_detection') {
          icon = <CarOutlined />;
        } else if (record.type === 'system') {
          icon = <SettingOutlined />;
        }
        return (
          <Space>
            {icon}
            {text}
          </Space>
        );
      },
    },
    {
      title: '级别',
      dataIndex: 'level',
      key: 'level',
      render: (level) => {
        let color = 'green';
        let text = '低';
        if (level === 'critical') {
          color = 'red';
          text = '紧急';
        } else if (level === 'high') {
          color = 'orange';
          text = '高';
        } else if (level === 'medium') {
          color = 'blue';
          text = '中';
        }
        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: '内容',
      dataIndex: 'content',
      key: 'content',
      ellipsis: true,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        if (status === 'unprocessed') {
          return <Badge status="error" text="未处理" />;
        } else if (status === 'processing') {
          return <Badge status="processing" text="处理中" />;
        } else if (status === 'processed') {
          return <Badge status="success" text="已处理" />;
        }
        return null;
      },
    },
    {
      title: '摄像头',
      dataIndex: 'cameraName',
      key: 'cameraName',
    },
    {
      title: '时间',
      dataIndex: 'timestamp',
      key: 'timestamp',
      sorter: (a, b) => moment(a.timestamp).unix() - moment(b.timestamp).unix(),
    },
    {
      title: '截图',
      dataIndex: 'screenshot',
      key: 'screenshot',
      render: (text) => text ? <img src={text} alt="截图" style={{ width: 80, height: 45, objectFit: 'cover' }} /> : '-',
    },
    {
      title: '操作',
      key: 'action',
      width: 180,
      render: (_, record) => {
        if (record.status === 'processed') {
          return (
            <Space size="small">
              <Button icon={<EyeOutlined />} size="small" onClick={() => handleView(record)}>查看</Button>
            </Space>
          );
        }
        return (
          <Space size="small">
            <Button 
              type="primary" 
              icon={<CheckCircleOutlined />} 
              size="small"
              onClick={() => handleProcess(record)}
              disabled={record.status === 'processed'}
            >
              处理
            </Button>
            <Button 
              icon={<EyeOutlined />} 
              size="small"
              onClick={() => handleView(record)}
            >
              查看
            </Button>
          </Space>
        );
      },
    },
  ];

  // 处理警告
  const handleProcess = (record) => {
    setSelectedRecord(record);
    setModalVisible(true);
  };

  // 查看警告
  const handleView = (record) => {
    Modal.info({
      title: '警告详情',
      content: (
        <div>
          <p><strong>类型：</strong>{record.typeName}</p>
          <p><strong>级别：</strong>{record.level}</p>
          <p><strong>内容：</strong>{record.content}</p>
          <p><strong>摄像头：</strong>{record.cameraName}</p>
          <p><strong>时间：</strong>{record.timestamp}</p>
          {record.screenshot && (
            <div>
              <p><strong>截图：</strong></p>
              <img src={record.screenshot} alt="截图" style={{ width: '100%', maxWidth: 400 }} />
            </div>
          )}
        </div>
      ),
      width: 600,
    });
  };

  // 处理警告提交
  const handleProcessSubmit = () => {
    setLoading(true);
    // 模拟API调用
    setTimeout(() => {
      message.success('处理成功');
      setLoading(false);
      setModalVisible(false);
    }, 1000);
  };

  // 获取不同状态的警告数量
  const getWarningCounts = () => {
    const counts = {
      all: warningData.length,
      unprocessed: warningData.filter(item => item.status === 'unprocessed').length,
      processing: warningData.filter(item => item.status === 'processing').length,
      processed: warningData.filter(item => item.status === 'processed').length,
    };
    return counts;
  };

  const warningCounts = getWarningCounts();

  // 根据状态筛选数据
  const getFilteredData = () => {
    if (activeTab === 'all') return warningData;
    return warningData.filter(item => item.status === activeTab);
  };

  return (
    <div>
      <Card title="警告管理">
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane 
            tab={
              <span>
                <BellOutlined />
                全部警告 ({warningCounts.all})
              </span>
            }
            key="all"
          />
          <TabPane 
            tab={
              <span>
                <ExclamationCircleOutlined />
                未处理 ({warningCounts.unprocessed})
              </span>
            }
            key="unprocessed"
          />
          <TabPane 
            tab={
              <span>
                <CloseCircleOutlined />
                处理中 ({warningCounts.processing})
              </span>
            }
            key="processing"
          />
          <TabPane 
            tab={
              <span>
                <CheckCircleOutlined />
                已处理 ({warningCounts.processed})
              </span>
            }
            key="processed"
          />
        </Tabs>

        <div style={{ marginBottom: 16 }}>
          <Space>
            <Select placeholder="选择警告类型" style={{ width: 150 }} allowClear>
              <Option value="person_detection">人流检测</Option>
              <Option value="fire_detection">火灾检测</Option>
              <Option value="vehicle_detection">车辆检测</Option>
              <Option value="system">系统警告</Option>
            </Select>
            <Select placeholder="选择警告级别" style={{ width: 150 }} allowClear>
              <Option value="critical">紧急</Option>
              <Option value="high">高</Option>
              <Option value="medium">中</Option>
              <Option value="low">低</Option>
            </Select>
            <Select placeholder="选择摄像头" style={{ width: 150 }} allowClear>
              <Option value="前门监控">前门监控</Option>
              <Option value="后门监控">后门监控</Option>
              <Option value="停车场监控">停车场监控</Option>
              <Option value="电梯监控">电梯监控</Option>
            </Select>
            <RangePicker showTime />
            <Button type="primary" icon={<SearchOutlined />}>搜索</Button>
            <Button>重置</Button>
          </Space>
        </div>

        <Table 
          columns={columns} 
          dataSource={getFilteredData()} 
          rowKey="id"
          loading={loading}
          pagination={{
            total: getFilteredData().length,
            pageSize: 10,
            showTotal: (total) => `共 ${total} 条`,
            showQuickJumper: true,
            showSizeChanger: true,
          }}
        />

        <Modal
          title="处理警告"
          visible={modalVisible}
          onOk={handleProcessSubmit}
          onCancel={() => setModalVisible(false)}
          confirmLoading={loading}
        >
          {selectedRecord && (
            <Form layout="vertical">
              <Form.Item label="警告类型">
                <Input value={selectedRecord.typeName} disabled />
              </Form.Item>
              <Form.Item label="警告内容">
                <Input.TextArea value={selectedRecord.content} disabled rows={3} />
              </Form.Item>
              <Form.Item label="处理结果" rules={[{ required: true }]}>
                <Input.TextArea rows={4} placeholder="请输入处理结果" />
              </Form.Item>
            </Form>
          )}
        </Modal>
      </Card>
    </div>
  );
};

export default WarningManagement;
